import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import GUI from 'lil-gui'
import HeroScene from './HeroScene.js'
// import Carousel from './Carousel.js'
import IntroSection from './IntroSection.js'
import LatestWork from './LatestWork.js'
import Footer from './Footer.js'
// import './carousel.css'
import './intro.css'
import './latestWork.css'
import './footer.css'

/**
 * Base
 */
// Debug
const gui = new GUI()
gui.hide() // Hide the GUI

// Canvas
const canvas = document.querySelector('canvas.webgl')
const fixedHeaderMode = canvas?.dataset?.fixedHeader === 'true'

// Scene
const scene = new THREE.Scene()
// Creamy beige background (very slight yellow tint)
scene.background = new THREE.Color(0xFFF8F0) // Warm white with slight cream/beige

/**
 * Hero Scene - Landing page meshes
 */
const heroScene = new HeroScene(scene)

// Load the hero scene asynchronously
heroScene.load().then(() => {
    console.log('Hero scene loaded successfully!')
}).catch(error => {
    console.error('Failed to load hero scene:', error)
})

/**
 * Intro Section - Presentation text
 */
const introSection = fixedHeaderMode ? null : new IntroSection()

/**
 * Latest Work - Project showcase
 */
const latestWork = fixedHeaderMode ? null : new LatestWork()

/**
 * Carousel - Image gallery
 */
// const carouselImages = [
//     { src: '/Images/Photos_elwwod.png', alt: 'Photo Elwood' },
//     { src: '/Images/photo_antho.jpg', alt: 'Photo Antho' },
//     { src: '/Images/photo_dublin.jpg', alt: 'Photo Dublin' },
//     { src: '/Images/projet_Solina.png', alt: 'Projet Solina' }
// ]

// const carousel = new Carousel(carouselImages)

// Get materials for GUI updates
const materials = heroScene.getMaterials()

// Debug controls
const debugObject = {
    // Layout parameters
    overallDistance: heroScene.layoutParams.overallDistance,
    horizontalSpread: heroScene.layoutParams.horizontalSpread,
    verticalSpread: heroScene.layoutParams.verticalSpread,
    depthVariation: heroScene.layoutParams.depthVariation,
    scale: heroScene.layoutParams.scale,
    horizontalOffset: heroScene.layoutParams.horizontalOffset,
    verticalOffset: heroScene.layoutParams.verticalOffset
}

// Layout controls folder
const layoutFolder = gui.addFolder('Layout')
layoutFolder.add(debugObject, 'overallDistance', 2, 20, 0.5).name('Overall Distance').onChange((value) => {
    heroScene.layoutParams.overallDistance = value
    heroScene.updateMeshPositions()
})

layoutFolder.add(debugObject, 'horizontalSpread', 2, 15, 0.5).name('Horizontal Spread').onChange((value) => {
    heroScene.layoutParams.horizontalSpread = value
    heroScene.updateMeshPositions()
})

layoutFolder.add(debugObject, 'verticalSpread', 1, 10, 0.5).name('Vertical Spread').onChange((value) => {
    heroScene.layoutParams.verticalSpread = value
    heroScene.updateMeshPositions()
})

layoutFolder.add(debugObject, 'depthVariation', 0, 10, 0.5).name('Depth Variation').onChange((value) => {
    heroScene.layoutParams.depthVariation = value
    heroScene.updateMeshPositions()
})

layoutFolder.add(debugObject, 'scale', 0.5, 5, 0.1).name('Scale').onChange((value) => {
    heroScene.layoutParams.scale = value
    heroScene.updateMeshPositions()
})

layoutFolder.add(debugObject, 'horizontalOffset', -10, 10, 0.1).name('Horizontal Offset').onChange((value) => {
    heroScene.layoutParams.horizontalOffset = value
    heroScene.updateMeshPositions()
})

layoutFolder.add(debugObject, 'verticalOffset', -10, 10, 0.1).name('Vertical Offset').onChange((value) => {
    heroScene.layoutParams.verticalOffset = value
    heroScene.updateMeshPositions()
})

layoutFolder.open()
/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

// Track current renderer height and FOV for smooth interpolation
let currentRendererHeight = sizes.height
let targetRendererHeight = sizes.height
const baseFOV = 75
let currentFOV = baseFOV
let targetFOV = baseFOV

window.addEventListener('scroll', () => {
    const scrollY = window.scrollY
    
    // Update intro section visibility (only on homepage)
    if (introSection) {
        introSection.updateVisibility(scrollY)
    }
    
    // Update carousel visibility
    // carousel.updateVisibility(scrollY)
    
    if (fixedHeaderMode) {
        const headerHeight = Math.round(sizes.height * FIXED_HEADER_SCALE)
        targetRendererHeight = headerHeight
        targetFOV = baseFOV * (headerHeight / sizes.height)
    } else if (scrollY > 50) {
        targetRendererHeight = 60
        // Decrease FOV when canvas is smaller to maintain scene size
        // FOV decreases proportionally to height decrease
        const heightRatio = 60 / sizes.height
        targetFOV = baseFOV * heightRatio
    } else {
        targetRendererHeight = sizes.height
        targetFOV = baseFOV
    }
})

const FIXED_HEADER_SCALE = 0.11 // doit correspondre au scaleY(0.11) dans le CSS

if (fixedHeaderMode)
{
    const headerHeight = Math.round(sizes.height * FIXED_HEADER_SCALE)
    targetRendererHeight = headerHeight
    currentRendererHeight = headerHeight
    const heightRatio = headerHeight / sizes.height
    targetFOV = baseFOV * heightRatio
    currentFOV = targetFOV
}

/**
 * Camera
 */
// Fixed camera - no movement
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 0, 0) // Fixed at origin
camera.rotation.set(0, 0, 0) // No rotation
scene.add(camera)

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    const effectiveHeight = fixedHeaderMode ? 60 : sizes.height

    // Update camera
    camera.aspect = sizes.width / effectiveHeight
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, effectiveHeight)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    
    // Recalculate responsive layout for new screen size
    const newParams = heroScene.calculateResponsiveLayout()
    Object.assign(heroScene.layoutParams, newParams)

    // Update shader resolution uniform for stable screen-space noise
    heroScene.updateResolution(sizes.width, effectiveHeight)

    // Update GUI to reflect new values
    Object.assign(debugObject, newParams)
    gui.controllersRecursive().forEach(controller => controller.updateDisplay())
    
    if (fixedHeaderMode)
    {
        const headerHeight = Math.round(sizes.height * FIXED_HEADER_SCALE)
        targetRendererHeight = headerHeight
        currentRendererHeight = headerHeight
        const heightRatio = headerHeight / sizes.height
        targetFOV = baseFOV * heightRatio
        currentFOV = targetFOV
    }

    // Update mesh positions with new responsive values
    heroScene.updateMeshPositions()
})

// Controls - disabled for fixed camera view
// const controls = new OrbitControls(camera, canvas)
// controls.enableDamping = true

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()
    
    // Smoothly interpolate renderer height and FOV
    const lerpSpeed = 0.1
    currentRendererHeight += (targetRendererHeight - currentRendererHeight) * lerpSpeed
    currentFOV += (targetFOV - currentFOV) * lerpSpeed
    
    // Only update if height or FOV has changed significantly
    const heightChanged = Math.abs(currentRendererHeight - canvas.height) > 1
    const fovChanged = Math.abs(currentFOV - camera.fov) > 0.1
    
    if (heightChanged || fovChanged) {
        // Update canvas resolution
        canvas.width = sizes.width
        canvas.height = Math.round(currentRendererHeight)
        
        // Update renderer
        renderer.setSize(sizes.width, Math.round(currentRendererHeight), false)
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        
        // Update camera FOV and aspect
        camera.fov = currentFOV
        camera.aspect = sizes.width / currentRendererHeight
        camera.updateProjectionMatrix()
        
        // Update shader resolution
        heroScene.updateResolution(sizes.width, Math.round(currentRendererHeight))
    }
    
    // Update hero scene animations
    heroScene.update(elapsedTime)

    // Update controls (disabled for fixed camera)
    // controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()

// Initialize footer (only on homepage)
if (!fixedHeaderMode) {
    const footer = new Footer()
}
